import isEmpty from 'lodash/isEmpty';
import { CheckIcon } from '@heroicons/react/outline';
import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import { QUESTION_TYPE__FREE_TEXT } from '@zedoc/questionnaire';
import Button from '../../../components/Button';
import Dialog from '../../../components/Dialog';
import { notifySuccess, notifyError } from '../../../utils/notify';
import { apiZedocPatientResponsesReview } from '../../../common/api/zedoc';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import FormFieldText from '../../../components/forms/FormFieldText';

const ReviewHistory = ({ activityId, milestone, activity }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [reviewNote, setReviewNote] = useState('');

  const dispatch = useDispatch();

  const updateResponseReview = (shouldSendNotification) => {
    return dispatch(
      callMethod(apiZedocPatientResponsesReview, {
        activityId,
        shouldSendNotification,
        // Only send notes when the reviewer typed something - the API schema
        // makes it optional, so omitting keeps the wire payload clean.
        ...(reviewNote && reviewNote.trim() && { notes: reviewNote.trim() }),
      }),
    );
  };

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const handleOnDialogOpen = () => {
    setReviewNote('');
    setIsDialogVisible(true);
  };
  const handleOnDialogClose = () => setIsDialogVisible(false);

  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [
    isSubmittingReviewAndSendNotification,
    setIsSubmittingReviewAndSendNotification,
  ] = useState(false);

  const reviews =
    activity &&
    activity.reviewHistory?.map((historyItem) => ({
      createdBy: historyItem.clinician,
      createdAt: new Date(historyItem.date),
      notes: historyItem.notes,
    }));

  const handleOnReview = () => {
    setIsSubmittingReview(true);
    updateResponseReview(false)
      .then(() => {
        setIsSubmittingReview(false);
        handleOnDialogClose();
      })
      .catch((error) => {
        setIsSubmittingReview(false);
        handleOnDialogClose();
        notifyError()(error.message);
      });
  };
  const handleOnReviewAndSendNotification = () => {
    setIsSubmittingReviewAndSendNotification(true);
    updateResponseReview(true)
      .then(() => {
        setIsSubmittingReviewAndSendNotification(false);
        handleOnDialogClose();
        notifySuccess(t('notificationSent'))();
      })
      .catch((error) => {
        setIsSubmittingReviewAndSendNotification(false);
        handleOnDialogClose();
        notifyError()(
          error.message ? error.message.split('[')[0] : t('notificationError'),
        );
      });
  };

  return (
    <>
      <div className="stack-4">
        <Button type="primary" onClick={handleOnDialogOpen}>
          Mark milestone as reviewed
        </Button>
        {!isEmpty(reviews) && (
          <div className="stack-1">
            <p className="font-medium">{t('reviewHistory')}</p>
            <ul>
              {reviews.map((review) => (
                <li className="stack-1">
                  <div className="cluster-1 items-center">
                    <CheckIcon className="w-5 h-5" />
                    <span>
                      {review.createdBy} on{' '}
                      {toIntlDateTimeFormat(language, review.createdAt, {
                        dateStyle: 'long',
                      })}
                    </span>
                  </div>
                  {review.notes && (
                    <p className="text-sm text-gray-600 pl-7 whitespace-pre-wrap">
                      {review.notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Dialog
        size="small"
        visible={isDialogVisible}
        title="Mark milestone as reviewed"
        onCancel={handleOnDialogClose}
        isCancelDisabled={
          isSubmittingReview || isSubmittingReviewAndSendNotification
        }
        footer={
          <>
            <Button
              onClick={handleOnReview}
              loading={isSubmittingReview}
              disabled={isSubmittingReviewAndSendNotification}
            >
              Just mark as reviewed
            </Button>
            <Button
              type="primary"
              onClick={handleOnReviewAndSendNotification}
              loading={isSubmittingReviewAndSendNotification}
              disabled={isSubmittingReview}
            >
              Mark and send notification
            </Button>
          </>
        }
      >
        <FormFieldText
          type={QUESTION_TYPE__FREE_TEXT}
          label={t('notes', { count: 1 })}
          rows={4}
          input={{
            value: reviewNote,
            onChange: (value) => setReviewNote(value),
          }}
          meta={{}}
        />
      </Dialog>
    </>
  );
};

export default ReviewHistory;
